import React, { useState } from 'react';
import { Play, ExternalLink, Figma } from 'lucide-react';
import '../styles/viewer.css';

// Transforme un lien Figma partagé en URL intégrable.
// `hide-ui` retire la barre d'outils Figma et `scaling` cadre la maquette :
// sans eux, l'embed s'ouvre sur le canevas dézoomé, avec l'interface de l'éditeur.
const toFigmaEmbed = (url) => {
  try {
    const u = new URL(url);
    u.hostname = 'embed.figma.com';
    u.searchParams.delete('t');
    u.searchParams.set('embed-host', 'share');
    u.searchParams.set('hide-ui', '1');
    u.searchParams.set('scaling', 'contain');
    u.searchParams.set('theme', 'dark');
    return u.toString();
  } catch {
    return null;
  }
};

const findLink = (links = [], isFigma) =>
  links.find((l) => l.href.includes('figma.com') === isFigma);

/**
 * Affiche l'artefact réel du projet : la maquette Figma manipulable, ou le site
 * en ligne. Le contenu tiers n'est chargé qu'au clic (motif « façade ») : la page
 * reste légère, et aucun cookie tiers n'est posé tant que l'utilisateur n'a rien
 * demandé.
 */
const ProjectViewer = ({ project }) => {
  const [live, setLive] = useState(false);

  const figmaLink = findLink(project.links, true);
  const siteLink = findLink(project.links, false);
  const isFigma = project.kind === 'figma' && figmaLink;

  const embedSrc = isFigma ? toFigmaEmbed(figmaLink.href) : siteLink?.href;
  const openHref = isFigma ? figmaLink.href : siteLink?.href;

  if (!embedSrc) {
    return (
      <figure className="viewer viewer--static">
        <img src={project.image} alt={`Aperçu du projet ${project.title}`} />
      </figure>
    );
  }

  return (
    <figure className="viewer">
      <div className="viewer-chrome">
        <span className="viewer-dots" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>

        <span className="viewer-url">
          {isFigma ? (
            <>
              <Figma size={13} aria-hidden="true" />
              Maquette Figma
            </>
          ) : (
            new URL(openHref).hostname
          )}
        </span>

        <a
          className="viewer-open"
          href={openHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          Ouvrir
          <ExternalLink size={13} aria-hidden="true" />
        </a>
      </div>

      <div className="viewer-stage">
        {live ? (
          <iframe
            src={embedSrc}
            title={
              isFigma
                ? `Maquette Figma du projet ${project.title}`
                : `Site en ligne — ${project.title}`
            }
            allowFullScreen
            loading="lazy"
          />
        ) : (
          <>
            <img
              src={project.image}
              alt={`Aperçu du projet ${project.title}`}
              className="viewer-poster"
            />
            <button type="button" className="viewer-play" onClick={() => setLive(true)}>
              <span className="viewer-play-icon" aria-hidden="true">
                <Play size={18} />
              </span>
              {isFigma ? 'Lancer la maquette' : 'Charger le site en direct'}
            </button>
          </>
        )}
      </div>

      <figcaption className="viewer-caption">
        {isFigma
          ? 'Maquette interactive — navigable directement ici.'
          : 'Site en ligne, chargé tel quel dans le cadre ci-dessus.'}
      </figcaption>
    </figure>
  );
};

export default ProjectViewer;
